import {
  Button,
  ErrorBoundary,
  Icon,
  ServerTable,
  type TableColumn,
  TableErrorDisplay,
  Text,
} from '@civilgrid/ui/components';
import {
  buildSearchPayload,
  createEmptyFilterGroup,
  deriveFilterTypesFromColumns,
  isEmptyFilterGroup,
  useTableSettings,
} from '@civilgrid/ui/hooks';
import { DEFAULT_PAGE_SIZE, type FilterGroupItem } from '@civilgrid/ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { ProjectsSearchPayload } from '~/api/projectsV3';
import authorizations, { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { getProjectRowLabel } from '~/common/projectUtils';
import type { OrganizationDetail, Project, TLoadedState } from '~/common/types';
import { InviteTeamProjectDialog } from '~/pages/common/Invite';
import { NewProjectButton } from '~/pages/common/TopNav/NewProject';
import rootStore from '~/store';
import { useHomeViewTableState } from '../../hooks';
import { prefetchProjectsQuery, useExportProjectsTable, useGetProjects } from '../../queries';
import {
  homeMapStore,
  homeViewStore,
  setBookmarksOnly,
  setMapGeometryFilter,
  setTableCount,
  setTableLoading,
  setTableSelection,
} from '../../store';
import type { ProjectViewMode } from '../../types';
import { EditDatesDialog, ReassignDialog, useBulkActions } from './BulkActions';
import { getProjectColumns } from './columns';

const TABLE_ID = 'projects-table';

function getColumnDefsForExport(cols: TableColumn<Project>[]) {
  return cols.map((col) => ({
    id: col.id ?? '',
    header: (col.header?.toString?.() ?? col.id ?? '') as string,
  }));
}

/**
 * Projects table view. Uses React Query with table state from homeViewStore
 * (pagination, sort, filters). Server returns { projects, count }; no client-side slice/filter.
 */
export function ProjectsTable({ viewMode, titleId }: { viewMode: ProjectViewMode; titleId?: string }) {
  const { currentOrganizationId, me, currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;
  const currentUserId = me?.id ?? null;

  // Table settings and state
  const { initialPageSize } = useTableSettings({ id: TABLE_ID });

  const orgMembers = useMemo(() => {
    if (currentOrganizationDetail.isLoading) return [];
    return (currentOrganizationDetail as TLoadedState & OrganizationDetail).members ?? [];
  }, [currentOrganizationDetail]);

  const queryClient = useQueryClient();
  const filterOptions = useMemo(() => ({ orgMembers }), [orgMembers]);
  const handleShowOnMap = useCallback((project: Project) => {
    homeMapStore.setFlyToProject(project);
  }, []);
  const columns = useMemo(
    () => getProjectColumns(filterOptions, viewMode === 'split' ? handleShowOnMap : undefined),
    [viewMode, filterOptions, handleShowOnMap],
  );
  const filterTypes = useMemo(() => deriveFilterTypesFromColumns(columns), [columns]);

  const stateControl = useHomeViewTableState({
    filterTypes,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    // Persisted page size seeds the store once; it is not the reset default.
    initialPagination:
      initialPageSize != null && initialPageSize !== DEFAULT_PAGE_SIZE
        ? { pageIndex: 0, pageSize: initialPageSize }
        : undefined,
  });

  const { pagination, sorting, filters, globalFilter, setFilters, setGlobalFilter, resetFilters } = stateControl;
  const { bookmarksOnly, mapGeometryFilter, tableSelection } = useSnapshot(homeViewStore);

  const buildSearchPayloadForBulk = useCallback(() => {
    const base = buildSearchPayload({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sorting,
      filters,
      globalFilter,
    });
    return {
      ...base,
      ...(bookmarksOnly && { bookmarkedOnly: true }),
      ...(mapGeometryFilter && {
        geometryFilter: mapGeometryFilter as import('geojson').MultiPolygon,
      }),
    };
  }, [pagination.pageIndex, pagination.pageSize, sorting, filters, globalFilter, bookmarksOnly, mapGeometryFilter]);

  const { data, isLoading, isFetching, error } = useGetProjects({
    organizationId: currentOrganizationId,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sorting,
    filters,
    globalFilter,
    bookmarkedOnly: bookmarksOnly,
    geometryFilter: mapGeometryFilter ? (mapGeometryFilter as import('geojson').MultiPolygon) : undefined,
    enabled: !!currentOrganizationId,
  });

  const projects = data?.projects ?? [];
  const rowCount = data?.count ?? 0;

  const getProjectById = useCallback(
    (id: string) => {
      return projects.find((proj) => proj.id === id);
    },
    [projects],
  );

  const bulk = useBulkActions({
    organizationId: currentOrganizationId,
    buildSearchPayloadForBulk,
    orgMembers,
    getProjectById,
  });

  const exportMutation = useExportProjectsTable(currentOrganizationId ?? '');

  // "My Projects" filter: projects where I am the project manager (projectManagerId = current user)
  const myProjectsFilter: FilterGroupItem = useMemo(
    () => ({
      type: 'group',
      id: `projectManagerId-filter-${Date.now()}`,
      operator: 'and',
      items: [
        {
          type: 'line',
          id: `projectManagerId-line-${Date.now()}`,
          operator: 'and',
          filterType: {
            id: 'projectManagerId',
            label: 'Project Manager',
          },
          operatorType: { id: 'eq', label: 'Equal' },
          value: currentUserId ?? undefined,
        },
      ],
    }),
    [currentUserId],
  );

  const isMyProjectsActive = useMemo(() => {
    if (!currentUserId || filters.items.length !== 1) return false;

    const filterItem = filters.items[0];
    if (filterItem.type !== 'line') return false;
    if (filterItem.filterType?.id !== 'projectManagerId') return false;
    if (filterItem.value !== currentUserId) return false;
    if (filterItem.operatorType?.id !== 'eq') return false;

    return true;
  }, [filters.items, currentUserId]);

  const handlePrefetchMyProjects = useCallback(() => {
    if (!currentUserId || !currentOrganizationId) return;
    prefetchProjectsQuery(queryClient, {
      organizationId: currentOrganizationId,
      pageIndex: 0,
      pageSize: pagination.pageSize,
      sorting,
      filters: myProjectsFilter,
    });
  }, [queryClient, currentOrganizationId, pagination.pageSize, sorting, myProjectsFilter, currentUserId]);

  const handlePrefetchBookmarks = useCallback(() => {
    if (!currentOrganizationId) return;
    prefetchProjectsQuery(queryClient, {
      organizationId: currentOrganizationId,
      pageIndex: 0,
      pageSize: pagination.pageSize,
      sorting,
      filters,
      globalFilter,
      bookmarkedOnly: true,
    });
  }, [queryClient, currentOrganizationId, pagination.pageSize, sorting, filters, globalFilter]);

  useEffect(() => {
    setTableCount(rowCount);
  }, [rowCount]);

  useEffect(() => {
    setTableLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  const tableData = useMemo(
    () => ({
      data: projects,
      rowCount,
      isLoading,
      isFetching,
      error: error ?? null,
    }),
    [projects, rowCount, isLoading, isFetching, error],
  );

  const handleClearAllFilters = useCallback(() => {
    resetFilters();
    setGlobalFilter('');
    setBookmarksOnly(false);
    setMapGeometryFilter(null);
  }, [resetFilters, setGlobalFilter]);

  const hasActiveFilters = useMemo(
    () =>
      !isEmptyFilterGroup(filters) ||
      (typeof globalFilter === 'string' && globalFilter.trim().length > 0) ||
      bookmarksOnly ||
      !!mapGeometryFilter,
    [filters, globalFilter, bookmarksOnly, mapGeometryFilter],
  );

  const isMyProjectsOnlyFilter = useMemo(() => {
    return isMyProjectsActive && !bookmarksOnly && !mapGeometryFilter && !globalFilter;
  }, [isMyProjectsActive, bookmarksOnly, mapGeometryFilter, globalFilter]);

  const emptyState = useMemo(() => {
    if (rowCount > 0 || isLoading || isFetching) return null;
    if (isMyProjectsOnlyFilter) {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <Text className="text-grey-80">Nothing has been assigned to you yet.</Text>
        </div>
      );
    }
    if (hasActiveFilters) {
      return (
        <div className="flex flex-col items-center gap-4 text-center">
          <Text className="text-grey-80">No records to show based on current filters.</Text>
          <Button
            variant="secondary"
            size="small"
            onClick={handleClearAllFilters}
            data-testid="home-projects-clear-all-filters"
          >
            <Icon icon="crossCircled" size={14} /> Clear all filters
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <Text className="text-grey-80">There are no projects yet. Create one now.</Text>
        <NewProjectButton data-testid="home-projects-empty-new-project" />
      </div>
    );
  }, [rowCount, isLoading, isFetching, isMyProjectsOnlyFilter, hasActiveFilters, handleClearAllFilters]);

  if (!currentOrganizationId) {
    return null;
  }

  return (
    <ErrorBoundary
      fallback={(err) => <TableErrorDisplay error={err} />}
      onError={(e, info) => console.error('ProjectsTable error:', e, info)}
    >
      <div className="flex h-full min-h-0 flex-col" data-testid="home-projects-table">
        <ServerTable
          id={TABLE_ID}
          aria-labelledby={titleId}
          columns={columns}
          data={tableData}
          stateControl={stateControl}
          enableColumnGrouping={false}
          getRowLabel={getProjectRowLabel}
          initialSelectionState={
            tableSelection.mode === 'partial'
              ? { mode: 'partial', selectedIds: [...tableSelection.selectedIds] }
              : { mode: 'all', excludedIds: [...tableSelection.excludedIds] }
          }
          onSelectionChange={setTableSelection}
          className="h-full"
          isLoading={tableData.isLoading}
          isFetching={tableData.isFetching}
          onMyFilters={() => {
            if (!currentUserId) return;
            if (isMyProjectsActive) {
              setFilters(createEmptyFilterGroup());
            } else {
              setFilters(myProjectsFilter);
            }
          }}
          onMyFiltersMouseEnter={handlePrefetchMyProjects}
          myFiltersLabel={
            <>
              <Icon icon="avatar" size={14} /> <Text className="text-xs">My Projects</Text>
            </>
          }
          isMyFiltersActive={isMyProjectsActive}
          onBookmarks={() => setBookmarksOnly(!bookmarksOnly)}
          onBookmarksMouseEnter={handlePrefetchBookmarks}
          isBookmarksActive={bookmarksOnly}
          customFilterPills={
            mapGeometryFilter
              ? [
                  {
                    id: 'mapGeometry',
                    label: 'Map filtering Table',
                    onRemove: () => setMapGeometryFilter(null),
                    labelOnly: true,
                  },
                ]
              : []
          }
          bulkActions={bulk.bulkActions}
          emptyState={emptyState}
          onExportAll={
            authorizations.can(Action.ViewOrgSettings)
              ? () => {
                  const columnDefs = getColumnDefsForExport(columns);
                  const search: ProjectsSearchPayload = buildSearchPayload({
                    pageIndex: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    sorting,
                  });
                  exportMutation.mutate({ columnDefs, search });
                }
              : undefined
          }
          onExportVisible={
            authorizations.can(Action.ViewOrgSettings)
              ? (params) => {
                  const columnDefs = getColumnDefsForExport(params.columns);
                  const baseSearch = buildSearchPayload({
                    pageIndex: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    sorting,
                    filters,
                    globalFilter,
                  });
                  const search: ProjectsSearchPayload = {
                    ...baseSearch,
                    ...(bookmarksOnly && { bookmarkedOnly: true }),
                    ...(mapGeometryFilter && {
                      geometryFilter: mapGeometryFilter as import('geojson').MultiPolygon,
                    }),
                  };
                  exportMutation.mutate({ columnDefs, search });
                }
              : undefined
          }
        />
      </div>
      <Can action={Action.EditProjectDetails} preventRender>
        <ReassignDialog
          open={bulk.reassign.open}
          onClose={bulk.reassign.close}
          projectManagerId={bulk.reassign.projectManagerId}
          onProjectManagerIdChange={bulk.reassign.setProjectManagerId}
          projectManagerOptions={bulk.reassign.projectManagerOptions}
          onSubmit={bulk.reassign.onSubmit}
          isPending={bulk.reassign.isPending}
        />
      </Can>
      <Can action={Action.EditProjectDetails} preventRender>
        <EditDatesDialog
          open={bulk.editDates.open}
          onClose={bulk.editDates.close}
          startDate={bulk.editDates.startDate}
          onStartDateChange={bulk.editDates.setStartDate}
          endDate={bulk.editDates.endDate}
          onEndDateChange={bulk.editDates.setEndDate}
          constructionStartDate={bulk.editDates.constructionStartDate}
          onConstructionStartDateChange={bulk.editDates.setConstructionStartDate}
          constructionEndDate={bulk.editDates.constructionEndDate}
          onConstructionEndDateChange={bulk.editDates.setConstructionEndDate}
          onSubmit={bulk.editDates.onSubmit}
          isPending={bulk.editDates.isPending}
        />
      </Can>
      {/**
       * TODO: Update permission to not use Action.ViewOrgSettings, shouldn't apply here
       * NOTE: preventRender keeps InviteTeamProjectDialog from mounting so it
       * doesn't try to incorrectly fetch org members when the dialog is closed.
       */}
      <Can action={[Action.ViewOrgSettings, Action.InviteOrgMembers, Action.AddProjectMember]} preventRender>
        <InviteTeamProjectDialog
          open={bulk.invite.open}
          onOpenChange={(o) => !o && bulk.invite.close()}
          projectId={bulk.invite.inviteDialogProps?.projectId}
          projectMemberUserIds={bulk.invite.inviteDialogProps?.projectMemberUserIds}
          onSave={bulk.invite.inviteDialogProps?.onSave}
          onBulkInvite={bulk.invite.inviteDialogProps?.onBulkInvite}
          isPending={bulk.invite.isPending}
        />
      </Can>
    </ErrorBoundary>
  );
}
